import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import moment from 'moment';
import BPromise from 'bluebird';
import Lazy from 'lazy.js';
import { History, Location } from 'history';
import { AppUser } from '../../../App/App';
import { SchoolEvent } from '../../../../models/event';
import { Child } from '../../../../models/child';
import { DEFAULT_RANGE_CALENDAR_YEARS, CALENDAR_FILTER } from '../../../../consts/calendar';
import {
  getYearRangeArray,
  isAllFilter,
  isClubEventsFilter,
  isSportEventsFilter,
  isTournamentEventsFilter
} from '../../../../helpers/calendar/calendar';
import { Loader } from '../../../../components/Loader/Loader';
import { ItemsBar } from './ItemsBar';
import { DaysOfWeekBar } from './DaysOfWeekBar';
import { MonthDaysPanel } from './MonthDaysPanel';
import { EventAndTournamentsTable } from './EventAndTournamentsTable';
import { CalendarFilterButtons } from './CalendarFilterButtons';
import { MonthYearSelector } from './MonthYearSelector';
import { getParentCalendarData } from '../../../../helpers/service/parent/childrenEvents';
import { getChildren } from '../../../../helpers/service/parent/children';
import { ParentPageHeader } from '../components/ParentPageHeader';
import './Calendar.scss';
import { parse } from 'query-string';

export type FilterType = 'SPORT_EVENTS' | 'CLUB_EVENTS' | 'TOURNAMENT_EVENTS' | 'ALL';
const CHILD_COLORS = ['#227ebd', '#2f9e5a', '#d4760c', '#c4313f', '#2496c1', '#7c3aed'];

function getChildColorByIndex(index: number): string {
  return CHILD_COLORS[index % CHILD_COLORS.length];
}

function getEventChildIds(event: SchoolEvent): string[] {
  const eventPlayers = propz.get(event, ['players'], []);
  return eventPlayers.map(player => player.userId);
}

function getChildEventCounts(children: Child[], events: SchoolEvent[]): Record<string, number> {
  return children.reduce((acc, child) => {
    acc[child.id] = events.filter(event => getEventChildIds(event).includes(child.id)).length;
    return acc;
  }, {} as Record<string, number>);
}

function getChildFormName(child: Child): string {
  return child.formName || propz.get(child, ['form', 'name'], '') || child.lastName;
}

type CalendarDateType = 'fixture' | 'club' | 'tournamentEvent';
type CalendarDateTypes = Record<string, CalendarDateType[]>;

function getCalendarDateKey(value: string | Date): string {
  const date = new Date(value);
  return date.getFullYear() + '-' + date.getMonth() + '-' + date.getDate();
}

function addCalendarDateTypes(map: CalendarDateTypes, values: string[], type: CalendarDateType): void {
  values.forEach(value => {
    const key = getCalendarDateKey(value);
    const current = map[key] || [];
    current.push(type);
    map[key] = current;
  });
}

function buildCalendarDateState(fixtureEventDates, clubEventDates, tournamentEventDates) {
  const dates = [...fixtureEventDates.dates, ...clubEventDates.dates, ...tournamentEventDates.dates];
  const eventDateTypes: CalendarDateTypes = {};

  addCalendarDateTypes(eventDateTypes, fixtureEventDates.dates, 'fixture');
  addCalendarDateTypes(eventDateTypes, clubEventDates.dates, 'club');
  addCalendarDateTypes(eventDateTypes, tournamentEventDates.dates, 'tournamentEvent');

  const datesUniq: string[] = Lazy(dates)
    .uniq()
    .toArray();

  return {
    dates: datesUniq.map(date => new Date(date)),
    eventDateTypes
  };
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface State {
  monthAndYearDate: Date;
  selectedDate: Date;
  nowDate: Date;
  fixtureEvents: SchoolEvent[];
  clubEvents: SchoolEvent[];
  tournamentEvents: SchoolEvent[];
  dates: Date[];
  eventDateTypes: CalendarDateTypes;
  activeMonthIndex: number;
  activeYearIndex: number;
  isLoading: boolean;
  filter: FilterType;
  children: Child[];
  childIdList: string[];
  isDateExplicitlySelected: boolean;
}

export class ParentCalendar extends Component<Props, State> {
  constructor(props) {
    super(props);

    const now = new Date();
    const nowMonth = now.getMonth();
    const nowYear = now.getFullYear();
    const monthAndYearDate = new Date(nowYear, nowMonth, 1);

    this.state = {
      monthAndYearDate,
      selectedDate: now,
      nowDate: now,
      activeMonthIndex: nowMonth,
      activeYearIndex: DEFAULT_RANGE_CALENDAR_YEARS,
      fixtureEvents: [],
      clubEvents: [],
      tournamentEvents: [],
      dates: [],
      eventDateTypes: {},
      isLoading: false,
      filter: CALENDAR_FILTER.ALL as FilterType,
      children: [],
      childIdList: [],
      isDateExplicitlySelected: false
    };
  }

  getEventPromises(filter: FilterType, monthAndYearDate: Date, selectedDate: Date, childIdList: string[]) {
    const { user } = this.props;

    const calendarData = getParentCalendarData(user, monthAndYearDate, selectedDate, childIdList, filter);

    const isAllOrSportEventsFilter = isAllFilter(filter) || isSportEventsFilter(filter);
    const isAllOrClubEventsFilter = isAllFilter(filter) || isClubEventsFilter(filter);
    const isAllOrTournamentEventsFilter = isAllFilter(filter) || isTournamentEventsFilter(filter);

    return [
      isAllOrSportEventsFilter ? calendarData.then(data => data.fixtureEventDates) : BPromise.resolve({ dates: [] }),
      isAllOrSportEventsFilter ? calendarData.then(data => data.fixtureEvents) : BPromise.resolve([]),

      isAllOrClubEventsFilter ? calendarData.then(data => data.clubEventDates) : BPromise.resolve({ dates: [] }),
      isAllOrClubEventsFilter ? calendarData.then(data => data.clubEvents) : BPromise.resolve([]),

      isAllOrTournamentEventsFilter
        ? calendarData.then(data => data.tournamentEventDates)
        : BPromise.resolve({ dates: [] }),
      isAllOrTournamentEventsFilter ? calendarData.then(data => data.tournamentEvents) : BPromise.resolve([])
    ];
  }

  componentDidMount() {
    const { monthAndYearDate, selectedDate, filter } = this.state;
    const { user, location } = this.props;
    const { state } = location;

    const filterFromHistory = propz.get(state, ['filter']);
    const selectedDateFromHistory = propz.get(state, ['selectedDate']);
    const isFilterFromHistoryExist = typeof filterFromHistory !== 'undefined';
    const isSelectedDateFromHistoryExist = typeof selectedDateFromHistory !== 'undefined';

    const currentFilter = isFilterFromHistoryExist ? filterFromHistory : filter;
    const currentDate = isSelectedDateFromHistoryExist ? selectedDateFromHistory : selectedDate;

    const now = new Date(currentDate);
    const nowMonth = now.getMonth();
    const nowYear = now.getFullYear();
    const currentMonthAndYearDate = new Date(nowYear, nowMonth, 1);

    this.setState({
      isLoading: true
    });

    let childIdList;
    let children;

    getChildren(user)
      .then(_children => {
        children = _children;
        const queryChildId = String(propz.get(parse(location.search || ''), ['childId'], '') || '');
        childIdList =
          queryChildId && children.some(child => child.id === queryChildId)
            ? [queryChildId]
            : children.map(child => child.id);
        const promises = [...this.getEventPromises(currentFilter, currentMonthAndYearDate, now, childIdList)];
        return BPromise.all(promises);
      })
      .then(
        ([fixtureEventDates, fixtureEvents, clubEventDates, clubEvents, tournamentEventDates, tournamentEvents]) => {
          const calendarDateState = buildCalendarDateState(fixtureEventDates, clubEventDates, tournamentEventDates);
          this.setState({
            isLoading: false,
            ...calendarDateState,
            fixtureEvents,
            clubEvents,
            tournamentEvents,
            children,
            childIdList,
            filter: currentFilter,
            selectedDate: now,
            isDateExplicitlySelected: isSelectedDateFromHistoryExist,
            monthAndYearDate: currentMonthAndYearDate,
            activeMonthIndex: nowMonth
          });
        }
      );
  }

  onNextMonthClick = (): void => {
    const { monthAndYearDate, activeYearIndex, filter, selectedDate, childIdList } = this.state;
    const nowMonth = monthAndYearDate.getMonth();
    const nextMonth = new Date(monthAndYearDate);
    nextMonth.setMonth(nowMonth + 1);
    const nextMonthIndex = nextMonth.getMonth();

    const promises = this.getEventPromises(filter, nextMonth, selectedDate, childIdList);

    this.setState({
      isLoading: true
    });

    if (nowMonth === 11) {
      if (activeYearIndex === DEFAULT_RANGE_CALENDAR_YEARS * 2) {
        this.setState({
          isLoading: false
        });
        return;
      }

      BPromise.all(promises).then(
        ([fixtureEventDates, fixtureEvents, clubEventDates, clubEvents, tournamentEventDates, tournamentEvents]) => {
          const calendarDateState = buildCalendarDateState(fixtureEventDates, clubEventDates, tournamentEventDates);
          this.setState({
            isLoading: false,
            ...calendarDateState,
            monthAndYearDate: nextMonth,
            activeMonthIndex: nextMonthIndex,
            activeYearIndex: activeYearIndex + 1
          });
        }
      );
    } else {
      BPromise.all(promises).then(
        ([fixtureEventDates, fixtureEvents, clubEventDates, clubEvents, tournamentEventDates, tournamentEvents]) => {
          const calendarDateState = buildCalendarDateState(fixtureEventDates, clubEventDates, tournamentEventDates);
          this.setState({
            isLoading: false,
            ...calendarDateState,
            monthAndYearDate: nextMonth,
            activeMonthIndex: nextMonthIndex
          });
        }
      );
    }
  };
  onPreviousMonthClick = (): void => {
    const { monthAndYearDate, activeYearIndex, filter, selectedDate, childIdList } = this.state;
    const nowMonth = monthAndYearDate.getMonth();
    const previousMonth = new Date(monthAndYearDate);
    previousMonth.setMonth(nowMonth - 1);
    const previousMonthIndex = previousMonth.getMonth();

    const promises = this.getEventPromises(filter, previousMonth, selectedDate, childIdList);

    this.setState({
      isLoading: true
    });

    if (nowMonth === 0) {
      if (activeYearIndex === 0) {
        this.setState({
          isLoading: false
        });
        return;
      }

      BPromise.all(promises).then(
        ([fixtureEventDates, fixtureEvents, clubEventDates, clubEvents, tournamentEventDates, tournamentEvents]) => {
          const calendarDateState = buildCalendarDateState(fixtureEventDates, clubEventDates, tournamentEventDates);
          this.setState({
            isLoading: false,
            ...calendarDateState,
            monthAndYearDate: previousMonth,
            activeMonthIndex: previousMonthIndex,
            activeYearIndex: activeYearIndex - 1
          });
        }
      );
    } else {
      BPromise.all(promises).then(
        ([fixtureEventDates, fixtureEvents, clubEventDates, clubEvents, tournamentEventDates, tournamentEvents]) => {
          const calendarDateState = buildCalendarDateState(fixtureEventDates, clubEventDates, tournamentEventDates);
          this.setState({
            isLoading: false,
            ...calendarDateState,
            monthAndYearDate: previousMonth,
            activeMonthIndex: previousMonthIndex
          });
        }
      );
    }
  };

  onMonthYearChange = (month: number, year: number): void => {
    const { filter, childIdList } = this.state;
    const nextMonth = new Date(year, month, 1);
    const promises = this.getEventPromises(filter, nextMonth, nextMonth, childIdList);
    const activeYearIndex = getYearRangeArray().findIndex(option => option === year);

    this.setState({ isLoading: true });

    BPromise.all(promises).then(
      ([fixtureEventDates, fixtureEvents, clubEventDates, clubEvents, tournamentEventDates, tournamentEvents]) => {
        const calendarDateState = buildCalendarDateState(fixtureEventDates, clubEventDates, tournamentEventDates);
        this.setState({
          isLoading: false,
          ...calendarDateState,
          selectedDate: nextMonth,
          monthAndYearDate: nextMonth,
          activeMonthIndex: month,
          activeYearIndex
        });
      }
    );
  };

  onMonthChange = (value: number): void => {
    const { monthAndYearDate, filter, selectedDate, childIdList } = this.state;
    const nextMonth = new Date(monthAndYearDate);
    nextMonth.setMonth(value);

    const promises = this.getEventPromises(filter, nextMonth, selectedDate, childIdList);

    this.setState({
      isLoading: true
    });

    BPromise.all(promises).then(
      ([fixtureEventDates, fixtureEvents, clubEventDates, clubEvents, tournamentEventDates, tournamentEvents]) => {
        const calendarDateState = buildCalendarDateState(fixtureEventDates, clubEventDates, tournamentEventDates);
        this.setState({
          isLoading: false,
          ...calendarDateState,
          monthAndYearDate: nextMonth,
          activeMonthIndex: Number(value)
        });
      }
    );
  };

  onYearChange = (value: number): void => {
    const { monthAndYearDate, filter, selectedDate, childIdList } = this.state;
    const nextYear = new Date(monthAndYearDate);
    nextYear.setFullYear(value);

    const promises = this.getEventPromises(filter, nextYear, selectedDate, childIdList);

    const yearRangeArray = getYearRangeArray();
    const activeYearIndex = yearRangeArray.findIndex(year => year === Number(value));

    this.setState({
      isLoading: true
    });

    BPromise.all(promises).then(
      ([fixtureEventDates, fixtureEvents, clubEventDates, clubEvents, tournamentEventDates, tournamentEvents]) => {
        const calendarDateState = buildCalendarDateState(fixtureEventDates, clubEventDates, tournamentEventDates);
        this.setState({
          isLoading: false,
          ...calendarDateState,
          monthAndYearDate: nextYear,
          activeYearIndex: activeYearIndex
        });
      }
    );
  };

  onDayClick = (date: Date): void => {
    const { filter, monthAndYearDate, childIdList } = this.state;
    this.setState({
      isLoading: true
    });

    const promises = this.getEventPromises(filter, monthAndYearDate, date, childIdList);

    BPromise.all(promises).then(
      ([fixtureEventDates, fixtureEvents, clubEventDates, clubEvents, tournamentEventDates, tournamentEvents]) => {
        this.setState({
          selectedDate: date,
          isDateExplicitlySelected: true,
          isLoading: false,
          fixtureEvents,
          clubEvents,
          tournamentEvents
        });
      }
    );
  };

  onFilterButtonClick = (filter: FilterType) => {
    const { monthAndYearDate, selectedDate, childIdList } = this.state;

    this.setState({
      isLoading: true
    });

    const promises = this.getEventPromises(filter, monthAndYearDate, selectedDate, childIdList);

    BPromise.all(promises).then(
      ([fixtureEventDates, fixtureEvents, clubEventDates, clubEvents, tournamentEventDates, tournamentEvents]) => {
        const calendarDateState = buildCalendarDateState(fixtureEventDates, clubEventDates, tournamentEventDates);
        this.setState({
          isLoading: false,
          ...calendarDateState,
          fixtureEvents,
          clubEvents,
          tournamentEvents,
          filter
        });
      }
    );
  };

  onEventClick = (eventId: string): void => {
    const { filter } = this.state;
    const { location } = this.props;
    const { pathname } = location;

    this.props.history.push({
      pathname: '/events/event',
      search: `id=${eventId}`,
      state: {
        filter,
        prevRoutePath: pathname
      }
    });
  };

  onChildClick = (childId: string) => {
    this.setState({
      isLoading: true
    });

    let childIdList;

    if (childId === 'all') {
      const { children } = this.state;
      childIdList = children.map(child => child.id);
    } else {
      childIdList = [childId];
    }

    const { filter, monthAndYearDate, selectedDate } = this.state;

    const promises = this.getEventPromises(filter, monthAndYearDate, selectedDate, childIdList);

    BPromise.all(promises).then(
      ([fixtureEventDates, fixtureEvents, clubEventDates, clubEvents, tournamentEventDates, tournamentEvents]) => {
        const calendarDateState = buildCalendarDateState(fixtureEventDates, clubEventDates, tournamentEventDates);
        this.setState({
          isLoading: false,
          ...calendarDateState,
          fixtureEvents,
          clubEvents,
          tournamentEvents,
          filter,
          childIdList
        });
      }
    );
  };

  getChildName = (child: Child) => {
    return `${child.firstName} ${child.lastName}`;
  };

  render() {
    const {
      activeMonthIndex,
      activeYearIndex,
      selectedDate,
      monthAndYearDate,
      nowDate,
      isLoading,
      fixtureEvents,
      clubEvents,
      tournamentEvents,
      dates,
      eventDateTypes,
      filter,
      children,
      childIdList,
      isDateExplicitlySelected
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const { user } = this.props;

    const events = [...fixtureEvents, ...clubEvents, ...tournamentEvents];
    const eventsSorted = events.sort((event1, event2) => {
      const eventStartTimeDate1 = new Date(event1.startTime);
      const eventStartTimeDate2 = new Date(event2.startTime);
      return Number(eventStartTimeDate1) - Number(eventStartTimeDate2);
    });

    const selectedDateTitle = moment(selectedDate).format('D MMMM YYYY');
    const selectedDateRelative = moment(selectedDate).isSame(nowDate, 'day')
      ? 'Today'
      : moment(selectedDate).format('dddd');
    const selectedItemsCount = eventsSorted.length;
    const childEventCounts = getChildEventCounts(children, eventsSorted);
    const childColors = children.reduce((acc, child, index) => {
      acc[child.id] = getChildColorByIndex(index);
      return acc;
    }, {} as Record<string, string>);
    return (
      <main className="parentPage parentCalendarPage">
        <ParentPageHeader
          title="Calendar"
          subtitle="Fixtures, clubs and tournament events for your children."
          chips={[
            { value: children.length, label: children.length === 1 ? 'child' : 'children', tone: 'blue' },
            {
              value: selectedItemsCount,
              label: selectedItemsCount === 1 ? 'event on selected day' : 'events on selected day',
              tone: selectedItemsCount ? 'amber' : 'slate'
            },
            { label: moment(monthAndYearDate).format('MMMM YYYY'), tone: 'slate' }
          ]}
        />
        <div className="cal-screen">
          <ItemsBar
            items={children}
            itemIdList={childIdList}
            onClick={this.onChildClick}
            allItemsTitle={'All children'}
            getItemName={this.getChildName}
            getItemMeta={getChildFormName}
            getItemColor={(child, index) => getChildColorByIndex(index)}
            itemCounts={childEventCounts}
            showItemCounts={isDateExplicitlySelected}
          />

          <CalendarFilterButtons user={user} filter={filter} onFilterButtonClick={this.onFilterButtonClick} />

          <div className="cal">
            <section className="cal-card cal-card--month">
              <MonthYearSelector
                activeMonthIndex={activeMonthIndex}
                activeYearIndex={activeYearIndex}
                onPreviousMonthClick={this.onPreviousMonthClick}
                onNextMonthClick={this.onNextMonthClick}
                onMonthYearChange={this.onMonthYearChange}
              />
              <DaysOfWeekBar />
              <MonthDaysPanel
                onClick={this.onDayClick}
                selectedDate={selectedDate}
                monthAndYearDate={monthAndYearDate}
                nowDate={nowDate}
                eventDates={dates}
                eventDateTypes={eventDateTypes}
              />
            </section>

            <section className="cal-card cal-card--events">
              <div className="cal-day__hd">
                <div>
                  <h2 className="cal-day__date">{selectedDateTitle}</h2>
                  <span className="cal-day__rel">· {selectedDateRelative}</span>
                </div>
                {selectedItemsCount > 0 ? (
                  <span className="cal-day__count">
                    {selectedItemsCount} event{selectedItemsCount === 1 ? '' : 's'}
                  </span>
                ) : null}
              </div>
              <div className="eEventAndTournamentsTableSection">
                <EventAndTournamentsTable
                  user={user}
                  events={eventsSorted}
                  children={children}
                  onEventClick={this.onEventClick}
                  childColors={childColors}
                />
              </div>
            </section>
          </div>
        </div>
      </main>
    );
  }
}
